// @ts-check
// Efeitos das ações da seleção (doc 06, seção 13), usados pela barra de seleção, pelo menu de comandos e pelo clique
// direito na linha ou no card. A API confere o papel em cada chamada; a tela só deixa de oferecer o que vai ser recusado.
import { fieldsById } from '../../data/metadata-store.js';
import { isManyToOneRelation } from '../../lib/field-metadata.js';
import { addFavorite, deleteManyRecords, restoreManyRecords } from '../../data/parity.js';
import { downloadRecordsCsv } from '../../data/dashboards.js';
import { downloadBlob, toFileName } from '../../lib/csv.js';
import { MAXIMUM_FAVORITES_AT_ONCE } from '../../lib/command-registry.js';
import { enqueueSnackbar, showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { getViewQuery } from '../views/view-state.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../data/metadata-store.js').View} View
 */

// tipos que não cabem numa célula do CSV: o servidor recusa a planilha inteira quando um deles é pedido
const NON_EXPORTABLE_FIELD_TYPES = new Set(['TS_VECTOR', 'MORPH_RELATION']);

/**
 * Nomes das colunas visíveis da view, na ordem, para o CSV sair como a pessoa está vendo. A coluna de uma relação
 * "um para muitos" (alunos do card) fica de fora, como na exportação sem colunas escolhidas.
 * @param {View} view
 */
export function getVisibleFieldNames(view) {
  return /** @type {string[]} */ ([...view.viewFields]
    .filter((viewField) => viewField.isVisible)
    .sort((first, second) => first.position - second.position)
    .map((viewField) => fieldsById.value.get(viewField.fieldMetadataId))
    .filter((field) => field && field.isActive !== false && !NON_EXPORTABLE_FIELD_TYPES.has(field.type) && !(field.type === 'RELATION' && !isManyToOneRelation(field)))
    .map((field) => field?.name));
}

/**
 * CSV dos selecionados: com "todos" marcado, o filtro da view (e o recorte do painel, quando a lista veio de um clique
 * num gráfico); senão os ids (a URL aguenta os 200 de uma página).
 * @param {{ objectMetadata: ObjectMetadata, view: View, ids: string[], isAllMatchingSelected: boolean }} input
 */
export async function exportRecordSelection({ objectMetadata, view, ids, isAllMatchingSelected }) {
  const query = getViewQuery(view);
  const filter = isAllMatchingSelected ? query.filter : { field: 'id', operator: 'in', value: ids };
  const { blob, fileName } = await downloadRecordsCsv(objectMetadata.namePlural, {
    filter, orderBy: query.orderBy.filter((item) => item.field !== 'position'), fields: getVisibleFieldNames(view), viewId: view.id,
    drilldown: isAllMatchingSelected ? query.drilldown : null,
  });
  downloadBlob(blob, fileName ?? toFileName(`${translateMetadataLabel(objectMetadata.labelPlural)} ${t('parity.bulk.selection')}`));
  showSuccessSnackbar(t('export.done'));
}

/** @param {{ objectMetadata: ObjectMetadata, ids: string[] }} input */
export async function favoriteRecordSelection({ objectMetadata, ids }) {
  const chosen = ids.slice(0, MAXIMUM_FAVORITES_AT_ONCE);
  for (const id of chosen) await addFavorite({ type: 'RECORD', objectNameSingular: objectMetadata.nameSingular, recordId: id });
  showSuccessSnackbar(t('parity.favorites.addedRecords', { count: chosen.length }));
}

/**
 * Manda para a lixeira com "Desfazer" na própria mensagem.
 * @param {{ objectMetadata: ObjectMetadata, ids: string[], onDeleted: (ids: string[]) => void, onChanged: () => void }} input
 */
export async function deleteRecordSelection({ objectMetadata, ids, onDeleted, onChanged }) {
  const result = await deleteManyRecords(objectMetadata, ids);
  const deletedIds = result.records.map((record) => record.id);
  onDeleted(deletedIds);
  const undo = () => restoreManyRecords(objectMetadata, deletedIds)
    .then((restored) => {
      onChanged();
      showSuccessSnackbar(t('parity.bulk.restored', { count: restored.records.length }));
    })
    .catch(showErrorSnackbar);
  if (result.errors.length > 0) {
    enqueueSnackbar({ message: t('parity.bulk.deletedWithErrors', { count: result.records.length, failed: result.errors.length }), detailedMessage: result.errors[0].message, variant: 'warning',
      ...(deletedIds.length > 0 ? { buttonLabel: t('parity.bulk.undo'), onButtonClick: undo } : {}) });
  } else {
    enqueueSnackbar({ message: t('parity.bulk.deleted', { count: result.records.length }), variant: 'success', buttonLabel: t('parity.bulk.undo'), onButtonClick: undo });
  }
}
